import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, NavLink, type To, useLocation } from 'react-router';

import type { ForumTag } from '~/api/forum/types';

import { forumTags } from '~/api/forum/constants';
import { useFavoriteForumTag } from '~/api/forum/useFavoriteForumTag';
import { useUser } from '~/api/user';
import { Card, Icon, type IconName, Illustration, Separator, Toggle } from '~/components';
import { SearchParamKeys } from '~/constants/url';
import { routes } from '~/providers/RouterProvider/router.routes';
import { clsx } from '~/styles/utils';
import { qs } from '~/utils/searchParams';
import { toast } from '~/utils/toast';

import { useForumTagFilter } from '../Forum.nuqs';
import { illustrationForForumTag } from '../Forum.utils';
import {
  favoriteTagToggleStyle,
  illustrationStyle,
  illustrationWrapperStyle,
  sectionItemsStyle,
  sectionItemStyle,
  sectionLinkIconStyle,
  sectionLinkStyle,
  sectionTitleStyle,
  sidebarStyle,
  tagItemActiveStyle,
  tagItemStyle,
  tagLinkStyle,
  wrapperStyle,
} from './ForumSidebar.css';

export const ForumSidebar = () => {
  const { data: user } = useUser();

  const favoriteTags = forumTags.filter((tag) => user.favoriteForumTags.includes(tag));
  const otherTags = forumTags.filter((tag) => !user.favoriteForumTags.includes(tag));

  const { t } = useTranslation(['forum']);

  return (
    <Card.Root className={sidebarStyle} render={<aside />} size="small">
      {favoriteTags.length > 0 && (
        <div className={sectionItemsStyle}>
          <div className={sectionTitleStyle}>{t('forum:sidebar.sections.favoriteTags')}</div>

          <ol className={sectionItemsStyle}>
            {favoriteTags.map((tag) => (
              <ForumTagItem isFavorite key={tag} tag={tag} />
            ))}
          </ol>
        </div>
      )}

      {otherTags.length > 0 && (
        <div className={sectionItemsStyle}>
          <div className={sectionTitleStyle}>{t('forum:sidebar.sections.tags')}</div>

          <ol className={sectionItemsStyle}>
            {otherTags.map((tag) => (
              <ForumTagItem isFavorite={false} key={tag} tag={tag} />
            ))}
          </ol>
        </div>
      )}

      <Separator />

      <div className={sectionItemsStyle}>
        <div className={sectionTitleStyle}>{t('forum:sidebar.sections.quickLinks')}</div>

        <SidebarLink icon="Link" to={routes.forumGuidelines}>
          {t('forum:guidelines.header.title')}
        </SidebarLink>
      </div>
    </Card.Root>
  );
};

const SidebarLink = ({ children, end, icon, to }: { children: ReactNode; end?: boolean; icon?: IconName; to: To }) => {
  return (
    <NavLink className={sectionLinkStyle} end={end} to={to}>
      <div className={wrapperStyle}>
        {icon && <Icon className={sectionLinkIconStyle} name={icon} size={32} />}

        <span>{children}</span>
      </div>
    </NavLink>
  );
};

const ForumTagItem = ({ isFavorite, tag }: { isFavorite: boolean; tag: ForumTag }) => {
  const [activeTag] = useForumTagFilter();

  const { pathname } = useLocation();

  const isActive = pathname === routes.forumTimeline && activeTag === tag;

  return (
    <li className={clsx(sectionItemStyle, tagItemStyle, isActive && tagItemActiveStyle)}>
      <SidebarTagLink isActive={isActive} tag={tag} />

      <FavoriteTagToggle isFavorite={isFavorite} tag={tag} />
    </li>
  );
};

const SidebarTagLink = ({ isActive, tag }: { isActive: boolean; tag: ForumTag }) => {
  const { t } = useTranslation(['forum']);

  return (
    <Link
      aria-current={isActive ? true : undefined}
      className={clsx(sectionLinkStyle, tagLinkStyle)}
      to={{
        pathname: routes.forumTimeline,
        search: isActive ? undefined : qs({ [SearchParamKeys.FILTER_FORUM_TAG]: tag }),
      }}
    >
      <div className={wrapperStyle}>
        <div className={clsx(sectionLinkIconStyle, illustrationWrapperStyle)}>
          <Illustration className={illustrationStyle} name={illustrationForForumTag(tag)} strokeWidth={2} />
        </div>

        <span>{t(`forum:tags.${tag}.name`)}</span>
      </div>
    </Link>
  );
};

const FavoriteTagToggle = ({ isFavorite, tag }: { isFavorite: boolean; tag: ForumTag }) => {
  const mutation = useFavoriteForumTag(tag);

  const { t } = useTranslation(['common', 'forum']);

  return (
    <Toggle
      aria-label={t('forum:sidebar.toggleFavoriteTag', { tag: t(`forum:tags.${tag}.name`) })}
      className={favoriteTagToggleStyle}
      disabled={mutation.isPending}
      icon="Star"
      onPressedChange={(pressed) =>
        mutation.mutate(pressed, {
          onError: () => {
            mutation.reset();
            toast.danger({ description: t('common:error') });
          },
        })
      }
      pressed={mutation.variables !== undefined ? mutation.variables : isFavorite}
      pressedIcon="StarFill"
    />
  );
};
